/**
 * Payment Service
 * Handles Stripe integration for CollegeAI Pro
 * Uses payment_manager_v2 (Firestore backend)
 */

// Stripe publishable key (safe to expose in frontend)
export const STRIPE_PUBLISHABLE_KEY = 'pk_live_51SqNZkIaK5CUG9YlONnCNLkWwwTYBD324TUpLXTVsnQwFaicXGj98w6NW7KRSGEe1H5eO3LJ347oePk7z6JwMk4N00IwF4aijO';

import { authedFetch } from './api';
import { track, EVENTS } from './analytics';
import { getFunnelOrigin } from './funnelOrigin';

// API endpoint - Payment Manager V2 (Firestore backend)
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_V2_API_URL || import.meta.env.VITE_PAYMENT_API_URL || 'https://payment-manager-v2-pfnwjfp26a-ue.a.run.app';

/**
 * Get user's current purchases and available credits
 */
export const getUserPurchases = async (userEmail) => {
    try {
        const response = await authedFetch(`${PAYMENT_API_URL}/purchases`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch purchases');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching purchases:', error);
        // Return default free tier
        return {
            success: true,
            purchases: {
                explorer_access: false,
                college_slots_available: 2,
                college_slots_total: 2,
                fit_analysis_available: 1,
                fit_analysis_total: 1,
                essay_strategy_available: 0,
                app_readiness_available: 0,
                ai_messages_available: 5,
                ai_unlimited: false,
                is_free_tier: true
            },
            purchase_history: []
        };
    }
};

/**
 * Create Stripe checkout session
 */
export const createCheckoutSession = async (userEmail, productId, quantity = 1, collegeId = null, extra = {}) => {
    try {
        // Same origin the pricing visit recorded, so the last funnel step
        // attributes to the surface that started it (#1220).
        // `from` (#4703, #4706): which offer started it, wall | parent_read |
        // pricing; the strategy's 90-day read splits purchases by it.
        // chosen_signed_out (#5207): the pass term was chosen before sign-in.
        track(EVENTS.CHECKOUT_STARTED, { product_id: productId, quantity,
                                         from_surface: getFunnelOrigin(),
                                         ...(extra.from ? { from: extra.from } : {}),
                                         ...(extra.chosenSignedOut ? { chosen_signed_out: true } : {}) });
        const response = await authedFetch(`${PAYMENT_API_URL}/checkout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
            body: JSON.stringify({
                product_id: productId,
                quantity,
                college_id: collegeId,
                user_email: userEmail,
                // The Patent Lab application is bound to one invention at
                // checkout (2026-09-22); other products never send this.
                ...(extra.researchId ? { research_id: extra.researchId } : {}),
                // Which offer started it (#4706): the server keeps only its
                // own enum (wall | parent_read | pricing) on the purchase.
                ...(extra.from ? { from: extra.from } : {}),
                // The market whose price the buyer was shown (#4966). A hint:
                // the server prices it only behind its flag, and charges a
                // local price only to a card issued there (ADR 0060).
                ...(extra.region ? { region: extra.region } : {}),
                // Through Graduation (#4986): the class year the page showed.
                // The server derives its own from the profile and refuses a
                // checkout whose year differs; it never takes the page's date.
                ...(extra.graduationYear ? { graduation_year: extra.graduationYear } : {}),
                // #5207: the term was chosen signed out. Analytics only: the
                // server's event carries it; eligibility never reads it.
                ...(extra.chosenSignedOut ? { chosen_signed_out: true } : {}),
            }),
        });

        const data = await response.json();

        if (data.setup_required) {
            // Stripe not yet configured
            return {
                success: false,
                setup_required: true,
                message: data.message || 'Payment system is being set up'
            };
        }

        if (data.checkout_url) {
            // Redirect to Stripe Checkout
            window.location.href = data.checkout_url;
            return { success: true, redirecting: true };
        }

        if (data.redirect_to_portal && data.url) {
            // Redirect to Customer Portal (Smart Upgrade)
            window.location.href = data.url;
            return { success: true, redirecting: true };
        }

        return data;
    } catch (error) {
        console.error('Error creating checkout:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Check if user has access to a specific feature
 */
export const checkAccess = async (userEmail, feature, collegeId = null) => {
    try {
        const params = new URLSearchParams({ feature });
        if (collegeId) params.append('college_id', collegeId);

        const response = await authedFetch(`${PAYMENT_API_URL}/check-access?${params}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
        });

        if (!response.ok) {
            // Default to free tier limits
            return getDefaultAccess(feature);
        }

        return await response.json();
    } catch (error) {
        console.error('Error checking access:', error);
        return getDefaultAccess(feature);
    }
};

/**
 * Use a credit (when performing an action)
 */
export const useCredit = async (userEmail, creditType, collegeId = null) => {
    try {
        const response = await authedFetch(`${PAYMENT_API_URL}/use-credit`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
            body: JSON.stringify({
                credit_type: creditType,
                college_id: collegeId,
                user_email: userEmail,
            }),
        });

        return await response.json();
    } catch (error) {
        console.error('Error using credit:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get available products and prices
 */
export const getProducts = async () => {
    try {
        const response = await authedFetch(`${PAYMENT_API_URL}/products`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return await response.json();
    } catch (error) {
        console.error('Error fetching products:', error);
        return { success: false, products: {} };
    }
};

/**
 * Cancel subscription (downgrade to free at period end)
 */
export const cancelSubscription = async (userEmail) => {
    try {
        const response = await authedFetch(`${PAYMENT_API_URL}/cancel-subscription`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
        });

        return await response.json();
    } catch (error) {
        console.error('Error canceling subscription:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Reactivate a scheduled subscription cancellation
 */
export const reactivateSubscription = async (userEmail) => {
    try {
        const response = await authedFetch(`${PAYMENT_API_URL}/reactivate-subscription`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
        });

        return await response.json();
    } catch (error) {
        console.error('Error reactivating subscription:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Get subscription status including cancellation details
 */
export const getSubscriptionStatus = async (userEmail) => {
    try {
        const response = await authedFetch(`${PAYMENT_API_URL}/subscription-status`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
        });

        return await response.json();
    } catch (error) {
        console.error('Error getting subscription status:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * The signed-in Season Pass holder's own refund answer (#4718): eligible or
 * not, why, and the counts the cancel modal says. Null on any failure: the
 * modal then shows the policy line, never a guess.
 */
export const getRefundEligibility = async (userEmail) => {
    try {
        const response = await authedFetch(`${PAYMENT_API_URL}/refund-eligibility`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-User-Email': userEmail,
            },
        });
        const body = await response.json();
        return body && body.success ? body.refund : null;
    } catch (error) {
        console.error('Error getting refund eligibility:', error);
        return null;
    }
};

/**
 * Whether the signed-in student may buy the Season Pass through graduation
 * (#4986), and the server's own end: {offered, reason, graduation_year,
 * ends_at, price: {currency, amount}} (rupees for `region` 'in' behind
 * india_masters). `surface` (pricing | upgrade_modal | trial_wall) is
 * where the switch would be drawn; the server counts the view when it is
 * offered. Resolves null when the route is dark (flag off: 404) or the read
 * failed: no switch, never a guess.
 */
export const getThroughGraduationOffer = async (userEmail, surface, region) => {
    try {
        const params = new URLSearchParams({ surface, user_email: userEmail, ...(region ? { region } : {}) });
        const response = await authedFetch(`${PAYMENT_API_URL}/through-graduation?${params}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
        });
        if (!response.ok) return null;
        const body = await response.json();
        return body && body.success ? body.through_graduation : null;
    } catch (error) {
        console.error('Error getting the Through Graduation offer:', error);
        return null;
    }
};

/**
 * What became of a held checkout (#4966, ADR 0060): the rupee Season Pass is
 * charged only once the card's issuing country is confirmed. Resolves
 * {state: 'paid' | 'declined' | 'pending', reason?}, or null when the read
 * failed (unknown, never "declined").
 */
export const getCheckoutOutcome = async (userEmail, sessionId) => {
    try {
        const params = new URLSearchParams({ session_id: sessionId, user_email: userEmail });
        const response = await authedFetch(`${PAYMENT_API_URL}/checkout-outcome?${params}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
        });
        const body = await response.json();
        return body && body.success ? { state: body.state, reason: body.reason || null } : null;
    } catch (error) {
        console.error('Error reading checkout outcome:', error);
        return null;
    }
};

/**
 * Default access for free tier (fallback)
 */
const getDefaultAccess = (feature) => {
    const defaults = {
        explorer: { has_access: false, reason: 'Explorer Pass required' },
        add_college: { has_access: true, reason: '2 slots available' },
        fit_analysis: { has_access: true, reason: '1 analysis available' },
        ai_chat: { has_access: true, reason: '5 messages remaining' },
    };

    return {
        success: true,
        ...(defaults[feature] || { has_access: false, reason: 'Unknown feature' }),
        feature
    };
};

export default {
    getUserPurchases,
    createCheckoutSession,
    checkAccess,
    useCredit,
    getProducts,
    cancelSubscription,
    reactivateSubscription,
    getSubscriptionStatus,
    getCheckoutOutcome,
    getThroughGraduationOffer,
};
